import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Filtro, IconoMas, Listado, type Columna } from '@tallerya/ui';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { usePermisos } from '../../permisos/usePermiso';
import { useSucursal } from '../../sucursal/ProveedorSucursal';
import { useAccionesGasto } from './AccionesGasto';
import { useListadoGastos, useMontosGastos, type VistaGastos, type Gasto, type MontoGasto } from './datos';
import { useFormatoGasto } from './formato';

// El listado de quien lleva la caja (#144): los gastos **pendientes de pago** y
// los **por completar** —sin monto o sin proveedor, lo que deja el mecánico con
// una foto y dos palabras—, de la sucursal del app bar. No tiene frame en el
// Figma: sigue el molde del listado de órdenes.
//
// - **Pide `expense.edit`**, el de quien paga y completa (decidido en #170): la
//   API deja leer con `expense.create`, pero esta pantalla no es para el mecánico.
// - **La vista vive en la URL** (`?vista=por-completar`), como los filtros de
//   las órdenes; la página no.
// - El «•••» es el de la sección de la orden (`useAccionesGasto`), y tocar la fila
//   abre la ficha del gasto (#189), con su historial; «Atrás» vuelve con la vista.
//   «Registrar gasto» carga uno **sin orden**: la renta, la luz.
// - Los montos de la página llegan en un pedido aparte, con `expense.amount.view`.

const VISTAS: readonly VistaGastos[] = ['por-pagar', 'por-completar', 'todos'];
const INICIAL: VistaGastos = 'por-pagar';

export function ListadoGastos() {
  const { t } = useTraduccion();
  useTituloPantalla(t('admin.gastos.listado.titulo'));
  const { actual, cargando } = useSucursal();

  if (cargando) return <p className="entrada">{t('app.cargando')}</p>;

  if (!actual) {
    return (
      <section className="aviso">
        <h2>{t('admin.ordenes.sinSucursal.titulo')}</h2>
        <p>{t('admin.gastos.listado.sinSucursal')}</p>
      </section>
    );
  }

  // Otra sucursal arriba empieza de nuevo, en la primera página.
  return <Contenido key={String(actual.id)} sucursal={String(actual.id)} />;
}

/**
 * Lo que le falta a un gasto para dejar de estar «por completar», como se lee en
 * su fila. `monto` es el leído, o `undefined` si no se sabe —sin permiso, o
 * todavía no llegó—.
 */
function queFalta(gasto: Gasto, monto: MontoGasto | undefined, t: ReturnType<typeof useTraduccion>['t']): string[] {
  const falta: string[] = [];
  if (monto !== undefined && monto.amount === null) falta.push(t('admin.gastos.listado.falta.monto'));
  if (gasto.supplierId === null) falta.push(t('admin.gastos.listado.falta.proveedor'));
  return falta;
}

function Contenido({ sucursal }: { sucursal: string }) {
  const { t } = useTraduccion();
  const { puede } = usePermisos();
  const navegar = useNavigate();
  const { dia, cifra } = useFormatoGasto();
  const veMontos = puede('expense.amount.view');

  const [params, setParams] = useSearchParams();
  const pedida = params.get('vista') ?? '';
  const vista = VISTAS.includes(pedida as VistaGastos) ? (pedida as VistaGastos) : INICIAL;
  const [cursor, setCursor] = useState<string | null>(null);
  // La API solo sabe ir hacia adelante: volver es sacar el último cursor de esta pila.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);

  // Otra vista —el filtro, «Atrás» o el menú— vuelve a la primera página.
  const [anterior, setAnterior] = useState(vista);
  if (vista !== anterior) {
    setAnterior(vista);
    setCursor(null);
    setAnteriores([]);
  }

  const consulta = useListadoGastos(sucursal, { vista, cursor });
  const pagina = consulta.data;
  const montos = useMontosGastos(pagina?.items.map((g) => String(g.id)) ?? [], veMontos);
  const montoDe = (g: Gasto): MontoGasto | undefined => (montos.isError ? undefined : montos.data?.get(String(g.id)));
  const { acciones, registrar, problema, ventanas } = useAccionesGasto({
    locationId: sucursal,
    serviceOrderId: null,
  });
  const verFicha = (g: Gasto) => void navegar(`/gastos/${String(g.id)}`, { state: { desde: 'listado' } });

  /**
   * Reemplaza la entrada del historial del navegador: «Atrás» sale del listado, no
   * deshace el filtro.
   */
  function elegir(nueva: string) {
    setParams(nueva === INICIAL ? {} : { vista: nueva }, { replace: true });
  }

  const columnas: Columna<Gasto>[] = [
    {
      id: 'gasto',
      titulo: t('admin.gastos.listado.columna.gasto'),
      // Con ancho: las demás lo tienen, y en el celular esta quedaba en una palabra.
      ancho: 240,
      celda: (g) => {
        const falta = vista === 'por-completar' ? queFalta(g, montoDe(g), t) : [];
        return (
          <span className="listado-gastos__gasto">
            {/* Un botón, para llegar con el teclado: la fila entera solo responde al dedo y al mouse. */}
            <button type="button" className="tipos__enlace" onClick={() => verFicha(g)}>
              {g.description}
            </button>
            {falta.length > 0 && (
              <span className="listado__aviso">{t('admin.gastos.listado.falta', { que: falta.join(', ') })}</span>
            )}
          </span>
        );
      },
      tarjeta: 'titulo',
    },
    {
      id: 'orden',
      titulo: t('admin.gastos.listado.columna.orden'),
      ancho: 150,
      // Sin orden es un gasto del taller: la renta, la luz.
      celda: (g) =>
        g.serviceOrderId ? (
          <Link className="tipos__enlace" to={`/ordenes/${String(g.serviceOrderId)}`}>
            {g.orderNumber ?? t('admin.ordenes.ficha.sinFolio')}
          </Link>
        ) : (
          <span className="listado__apagado">{t('admin.gastos.listado.delTaller')}</span>
        ),
      tarjeta: 'pie',
    },
    {
      id: 'proveedor',
      titulo: t('admin.gastos.formulario.proveedor'),
      ancho: 190,
      celda: (g) => g.supplierName ?? <span className="listado__aviso">{t('admin.gastos.formulario.sinProveedor')}</span>,
      tarjeta: 'renglon',
    },
    {
      id: 'compra',
      titulo: t('admin.gastos.listado.columna.compra'),
      ancho: 130,
      celda: (g) => <span className="listado__apagado">{dia(g.purchasedOn)}</span>,
      // «Compra hoy»: junto a la del pago, una fecha suelta no dice de qué es.
      tarjeta: { lugar: 'pie', conTitulo: true, orden: 2 },
    },
    {
      id: 'pago',
      titulo: t('admin.gastos.listado.columna.pago'),
      ancho: 170,
      celda: (g) => (
        <span className={`orden__marca orden__marca--${g.paidOn ? 'neutro' : 'aviso'}`}>
          {g.paidOn ? t('admin.gastos.pagado', { fecha: dia(g.paidOn) }) : t('admin.gastos.pendiente')}
        </span>
      ),
      // Primero en el pie: si se recorta, que no sea lo que la caja vino a ver.
      tarjeta: { lugar: 'pie', orden: 1 },
    },
    ...(veMontos
      ? [
          {
            id: 'monto',
            titulo: t('admin.gastos.formulario.monto'),
            ancho: 130,
            alinear: 'fin' as const,
            // Mientras llega no se dibuja: un «Sin monto» que dura un instante confunde.
            celda: (g: Gasto) => {
              const monto = montoDe(g);
              if (!monto) return null;
              const texto = cifra(monto);
              return texto ?? <span className="listado__aviso">{t('admin.gastos.sinMonto')}</span>;
            },
            // Un gasto no tiene estado: lo que más decide es cuánto fue.
            tarjeta: 'esquina' as const,
          },
        ]
      : []),
  ];

  const botonRegistrar = (
    <Boton icono={<IconoMas />} onClick={registrar}>
      {t('admin.gastos.registrar')}
    </Boton>
  );

  return (
    <>
      {problema && <AvisoError aviso={{ titulo: t('admin.gastos.noSePudo'), texto: problema }} />}

      <Listado
        titulo={t('admin.gastos.listado.titulo')}
        info={t('admin.gastos.listado.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(g) => String(g.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        onFila={verFicha}
        filtros={
          <Filtro
            etiqueta={t('admin.gastos.listado.filtro')}
            valor={vista}
            onCambiar={elegir}
            opciones={[
              { valor: 'por-pagar', etiqueta: t('admin.gastos.listado.porPagar') },
              { valor: 'por-completar', etiqueta: t('admin.gastos.listado.porCompletar') },
              { valor: 'todos', etiqueta: t('admin.gastos.listado.todos') },
            ]}
          />
        }
        acciones={botonRegistrar}
        // Cada vista tiene su vacío propio: elegir una no es un filtro que limpiar.
        // Solo cuenta otra página, que se vacía al completar o pagar lo último de
        // ella; ahí se vuelve a la primera, en la misma vista.
        hayFiltros={cursor !== null}
        onLimpiarFiltros={() => {
          setCursor(null);
          setAnteriores([]);
        }}
        sugerenciaSinResultados={t('admin.gastos.listado.paginaVacia')}
        vacio={{
          titulo: t(`admin.gastos.listado.vacio.${vista}`),
          texto: t('admin.gastos.listado.vacio.texto'),
          acciones: botonRegistrar,
        }}
        opcionesFila={(g) => [
          { etiqueta: t('admin.gastos.verFicha'), onSeleccionar: () => verFicha(g) },
          ...acciones(g, montoDe(g)),
        ]}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setCursor(anteriores.at(-1) ?? null);
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            if (!pagina?.nextCursor) return;
            setAnteriores((a) => [...a, cursor]);
            setCursor(pagina.nextCursor);
          },
        }}
      />

      {ventanas}
    </>
  );
}
